import React, { useState, useEffect } from 'react';
import { Save } from 'lucide-react';
import { $api } from '../../api';
import ErrorMessage from '../ErrorMessage';
import SuccessMessage from '../SuccessMessage';

interface ProfileData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  company_name: string;
}

const ProfileSettings: React.FC = () => {
  const [formData, setFormData] = useState<ProfileData>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company_name: '',
  });
  const [success, setSuccess] = useState<string | null>(null);

  // useQuery for profile data
  const profileQuery = $api.useQuery('get', '/api/settings/profile', {
    queryKey: ['profile'],
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // useMutation for profile update
  const updateProfileMutation = $api.useMutation('put', '/api/settings/profile');

  useEffect(() => {
    if (profileQuery.data) {
      setFormData({
        first_name: profileQuery.data.first_name,
        last_name: profileQuery.data.last_name,
        email: profileQuery.data.email,
        phone: profileQuery.data.phone || '',
        company_name: profileQuery.data.company_name,
      });
    }
  }, [profileQuery.data]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSuccess(null);

    updateProfileMutation.mutate(
      {
        body: {
          first_name: formData.first_name,
          last_name: formData.last_name,
          phone: formData.phone || undefined,
          company_name: formData.company_name,
        },
      },
      {
        onSuccess: (data) => {
          setSuccess(data.message || 'Profil mis à jour avec succès');
          profileQuery.refetch();
        },
      }
    );
  };

  if (profileQuery.isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Chargement...</span>
        </div>
      </div>
    );
  }

  const displayError = profileQuery.error || updateProfileMutation.error;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Informations du Profil</h3>
      
      {displayError && (
        <ErrorMessage error={displayError} className="mb-4" />
      )}

      {success && (
        <SuccessMessage message={success} className="mb-4" />
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Prénom *</label>
            <input
              type="text"
              value={formData.first_name}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Nom *</label>
            <input
              type="text"
              value={formData.last_name}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
          <input
            type="email"
            value={formData.email}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 cursor-not-allowed"
            disabled
            title="L'email ne peut pas être modifié"
          />
          <p className="text-xs text-gray-500 mt-1">L'adresse email ne peut pas être modifiée</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone</label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="+32 123 456 789"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Entreprise *</label>
          <input
            type="text"
            value={formData.company_name}
            onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>
        <button 
          type="submit"
          disabled={updateProfileMutation.isPending}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {updateProfileMutation.isPending ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Sauvegarde...</span>
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              <span>Sauvegarder</span>
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default ProfileSettings;

